// @flow
import React, {useRef, useEffect, useState} from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import type { Styles } from 'shared/types';
import Icon from '@ff/ui/Icon';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

interface Props {
  activePage: number,
  activeYear: number,
  pageCount: number,
  getMorePosts: (page: number, year: number) => void
}

export default function ArticlePagination(props: Props){
  const {
    activePage,
    activeYear,
    pageCount,
    getMorePosts
  } = props;

  const nextPageBtnWidth = 16; //16px

  const c: Styles<typeof styles> = useStyles(props);

  const pageRefEls = useRef([]);
  const containerRef = useRef();
  const nextPageListBtnRef = useRef();

  const [pageEls, setPageEls] = useState();
  const [containerWidth, setContainerWidth] = useState();
  const [firstPage, setFirstPage] = useState(activePage || 0);
  const [lastPage, setLastPage] = useState();
  const [pageNumMargin, setPageNumMargin] = useState();
  const [nextPageListBtnWidth, setNextPageListBtnWidth] = useState();

  useEffect(() => {
    const measureContainerWidth = () => {
      setContainerWidth(containerRef.current?.offsetWidth)
    }

    const measureNumWidth = () => {
      !!nextPageListBtnRef.current?.offsetWidth &&
        setNextPageListBtnWidth(pre => pre || nextPageListBtnRef.current?.offsetWidth);
        setPageEls(pre => pre?.length > 0 ? pre : pageRefEls.current.map((p) => p.offsetWidth))
        if (pageRefEls.current.length > 1) {
          // if there is 1 page only, we don't need pagination.
          setPageNumMargin(pre => pre ||
            pageRefEls.current[1].offsetLeft -
              pageRefEls.current[0].offsetLeft -
              pageRefEls.current[0].offsetWidth
          );
        }
    };

    const measureWdith = () => {
      measureContainerWidth();
      measureNumWidth();
    }

    window.addEventListener('load', measureWdith);
    window.addEventListener('resize', measureContainerWidth);

    measureWdith();

    return () => {
      window.removeEventListener('resize', measureContainerWidth);
      window.removeEventListener('load', measureWdith);
    };
  }, [])

  const goBack = () => {
    if (activePage !== 0) {
      getMorePosts(activePage - 1, activeYear);
    }
  };
  const goToPage = (page) => {
    if (activePage !== page) {
      getMorePosts(page, activeYear);
    }
  };
  const goNext = () => {
    if (activePage !== pageCount - 1) {
      getMorePosts(activePage + 1, activeYear);
    }
  };

  const paginationGoNext = () => {
    if(pages.length > 0){
      let last = pages[pages.length - 1];
      if(last < pageCount - 1){
        setFirstPage(last + 1);
        setLastPage(null);
      }
    }
  }

  const paginationGoBack = () => {
    if(pages.length > 0){
      let first = pages[0];
      if(first > 0){
        setLastPage(first - 1);
      }
    }
  }

  // Create list of page indices
  // please see comment in YearFilter component for details
  // If actualFirstPage is 3, actualLasterPage is 6, the pages will be [3,4,5,6]
  // That means the pagination list will be ... 4 5 6 7 ...
  let pages = null;
  let actualFirstPage;
  let actualLastPage;

  // check if all elements width has been measured.
  if(pageEls?.length > 0 && containerWidth > 0 && pageNumMargin !== null){
    // masPageListWidth is the full page number list width without pagination.
    const maxPageListWidth = pageEls.reduce((a, b) => a + b + pageNumMargin) + pageNumMargin;
    // maxPageListContainerWidth is the max dev width to display page number list
    const maxPageListContainerWidth = containerWidth - nextPageBtnWidth * 2;

    if(maxPageListContainerWidth <= maxPageListWidth){ // the container width is not enough, need pagination
      //pageListContainerWidth is the div width which can be used to display a page number list.
      const pageListContainerWidth = maxPageListContainerWidth - (nextPageListBtnWidth + pageNumMargin) * 2;
      // The restPageListContainerWidth is used to calculate how many page numbers we can displayed in the div.
      let restPageListContainerWidth = pageListContainerWidth;

      // Calculate the page numbers will be displayed in the pagination div.
      if (lastPage != null) {
        // calculate by given last page number.
        // For example, if last page is 10, the pagincation view can disply 5 pages, the pages will be [6,7,8,9,10], the dispalyed numbers will be ...7 8 9 10 11...
        // if last page is 3, the pagincation view can disply 6 pages, the pages will be [0,1,2,3,4,5] the result will be ...1 2 3 4 5 6...
        actualLastPage = lastPage;

        for (let i = lastPage; i >= 0; i--) {
          if (restPageListContainerWidth > pageEls[i] + pageNumMargin) {
            actualFirstPage = i;
            restPageListContainerWidth -= pageEls[i] + pageNumMargin;
          }
          else{
            break;
          }
        }

        if (actualFirstPage == 0) {
          for (let i = lastPage + 1; i < pageCount; i++) {
            if (restPageListContainerWidth > pageEls[i] + pageNumMargin) {
              actualLastPage = i;
              restPageListContainerWidth -= pageEls[i] + pageNumMargin;
            }
            else{
              break;
            }
          }
        }
      } else {
      // calculate the page number by first page number
        // For example, if first page is 5, the pagincation view can disply 6 pages, the pages will be [5,6,7,8,9,10], the dispalyed numbers will be ...6 7 8 9 10 11...
        // if first page is 8, the pagincation view can disply 6 pages, there are 10 page numbers total, the pages will be [4,5,6,7,8,9] the result will be ...5 6 7 8 9 10...
        actualFirstPage = firstPage;

        for (let i = firstPage; i < pageCount; i++) {
          if (restPageListContainerWidth > pageEls[i] + pageNumMargin) {
            actualLastPage = i;
            restPageListContainerWidth -= pageEls[i] + pageNumMargin;
          }
          else{
            break;
          }
        }

        if (actualLastPage == pageCount - 1) {
          for (let i = firstPage - 1; i >= 0; i--) {
            if (restPageListContainerWidth > pageEls[i] + pageNumMargin) {
              actualFirstPage = i;
              restPageListContainerWidth -= pageEls[i] + pageNumMargin;
            }
            else{
              break;
            }
          }
        }
      }

      pages = [];
      if(actualFirstPage >=0 && actualLastPage >= 0){
        for (let i = actualFirstPage; i <= actualLastPage; i ++){
          pages.push(i)
        }
      }
    }
  }

  if (!pages) {
    pages = [];
    for (let i = 0; i < pageCount; i++) {
      pages.push(i);
    }
  }

  return (
    <>
      {pages.length > 0 && pages.length < pageCount && (
        <div className={c.paginationInfo}>
          {activePage + 1} / {pageCount}
        </div>
      )}
      <div ref={containerRef} className={c.pagination}>
        <Icon
          name="ff-icon-arrow-paginate-back"
          className={classNames( c.paginationIcon, activePage === 0 && c.disabledIcon)}
          onClick={goBack}
        />
        <div className={c.pages}>
          { (!nextPageListBtnWidth || pages.length > 0 && pages.length < pageCount) && (
            <a
              ref={nextPageListBtnRef}
              className={classNames( pages[0] === 0 && c.disabledIcon, c.pageButton, c.nextPageListButton)}
              onClick={paginationGoBack}
            >
              ...
            </a>
          )}
          {pages.map(page => (
            <a
              ref={(e) => pageRefEls.current.length >= pageCount ||
                pageRefEls.current.push(e)
              }
              key={page}
              className={classNames( activePage === page && c.active, c.pageButton)}
              onClick={() => { goToPage(page); }}
            >
              {page + 1}
            </a>
          ))}
          {pages.length > 0 && pages.length < pageCount && (
            <a
              className={classNames( pages[pages.length - 1] >= pageCount - 1 && c.disabledIcon, c.pageButton, c.nextPageListButton)}
              onClick={paginationGoNext}
            >
              ...
            </a>
          )}
        </div>
        <Icon
          name="ff-icon-arrow-paginate-next"
          className={classNames(
            c.paginationIcon,
            activePage === pageCount - 1 && c.disabledIcon
          )}
          onClick={goNext}
        />
      </div>
    </>
  );
}

const styles = {
  paginationInfo: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '36px',
    marginBottom: '-18px',
    width: '100%',
    fontFamily: FONT_FAMILY_ROBOTO,
    fontSize: '15px',
    lineHeight: '1.05em',
    letterSpacing: '.1em'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '36px 0px',
    width: '100%'
  },
  pages: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  paginationIcon: {
    fontSize: '16px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  pageButton: {
    fontFamily: FONT_FAMILY_ROBOTO,
    fontWeight: '600',
    fontSize: '15px',
    lineHeight: '1.05em',
    letterSpacing: '.1em',
    marginLeft: '20px',
    marginRight: '20px',
    marginBottom: '12px',
    cursor: 'pointer'
  },
  nextPageListButton: {
    width: '18px'
  },
  disabledIcon: {
    color: '#cfd3d6',
    cursor: 'auto'
  },
  active: {
    color: '#c9c9c9',
    cursor: 'auto'
  },
};


const useStyles = createUseStyles(styles);
